/**
 * Helper functions for booking payment summary display.
 * Keeps logic out of JSX.
 */

export interface PaymentSummaryInput {
  totalPaidOre: number;
  totalStayOre: number | null;
  prepaymentAmountOre: number | null;
  requiresPrepayment: boolean;
  paymentCount: number;
}

export interface PaymentSummary {
  /** e.g. "Slutbetald · 1 800 kr" or "Betalt hittills: 800 kr" */
  label: string;
  /** e.g. "Återstår: 1 000 kr" or null */
  remainingLabel: string | null;
  /** Amount in öre remaining */
  remainingOre: number;
  /** Whether fully paid */
  isFullyPaid: boolean;
  /** Suggested prefill for next payment in öre */
  suggestedNextPaymentOre: number;
}

function fmtKr(ore: number): string {
  return Math.round(ore / 100).toLocaleString('sv-SE') + ' kr';
}

export function getBookingPaymentSummary(input: PaymentSummaryInput): PaymentSummary {
  const { totalPaidOre, totalStayOre, prepaymentAmountOre, requiresPrepayment, paymentCount } = input;

  const totalOre = totalStayOre ?? 0;
  const remainingOre = Math.max(0, totalOre - totalPaidOre);
  const isFullyPaid = totalOre > 0 && totalPaidOre >= totalOre;

  // Suggested prefill
  let suggestedNextPaymentOre = remainingOre;
  if (requiresPrepayment && prepaymentAmountOre && totalPaidOre < prepaymentAmountOre) {
    suggestedNextPaymentOre = prepaymentAmountOre - totalPaidOre;
  }

  if (isFullyPaid) {
    return {
      label: `Slutbetald · ${fmtKr(totalPaidOre)}`,
      remainingLabel: null,
      remainingOre: 0,
      isFullyPaid: true,
      suggestedNextPaymentOre: 0,
    };
  }

  if (totalPaidOre > 0) {
    return {
      label: `Betalt hittills: ${fmtKr(totalPaidOre)}`,
      remainingLabel: totalOre > 0 ? `Återstår: ${fmtKr(remainingOre)}` : null,
      remainingOre,
      isFullyPaid: false,
      suggestedNextPaymentOre,
    };
  }

  return {
    label: paymentCount === 0 ? 'Ingen betalning registrerad' : '',
    remainingLabel: totalOre > 0 ? `Totalt: ${fmtKr(totalOre)}` : null,
    remainingOre,
    isFullyPaid: false,
    suggestedNextPaymentOre: suggestedNextPaymentOre || (prepaymentAmountOre ?? totalOre),
  };
}
